import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface WanderfulHeroProps {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  cta1Text?: string;
  cta1Link?: string;
  cta2Text?: string;
  cta2Link?: string;
}

export const WanderfulHero = ({
  eyebrow = "AI ANSWER SHEET EVALUATION • BUILT BY IIT ALUMNI • MADE IN INDIA",
  title = "Mark 200 Papers in 30 Minutes.",
  subtitle = "Parakh AI evaluates handwritten subjective answer sheets — UPSC, JEE/NEET, CBSE, university — with 95% accuracy, in Hindi & English, at ₹0.40 per page.",
  cta1Text = "Get Started",
  cta1Link = "/support",
  cta2Text = "Watch Demo",
  cta2Link = "#video-section"
}: WanderfulHeroProps) => {
  const bgRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // GSAP Mouse Parallax with Lerp
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const onMouseMove = (e: MouseEvent) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      targetX = ((e.clientX - cx) / cx) * 20;
      targetY = ((e.clientY - cy) / cy) * 20;
    };

    let rafId: number;
    const animate = () => {
      currentX += (targetX - currentX) * 0.06;
      currentY += (targetY - currentY) * 0.06;

      if (bgRef.current) {
        gsap.set(bgRef.current, { x: currentX, y: currentY });
      }

      rafId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', onMouseMove);
    rafId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div 
      className="min-h-screen bg-black text-white overflow-hidden relative flex flex-col justify-end md:justify-center items-center px-4 sm:px-6 pt-24 pb-16 md:pt-40 md:pb-12"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Background Image with Parallax */}
      <div ref={bgRef} className="absolute inset-0 z-0 scale-[1.08] origin-center">
        <img 
          src="/ish_image_aisa_kro_ki_202605111947.jpeg" 
          alt="Background" 
          className="w-full h-full object-cover"
        />
        {/* Dark Overlay to make text readable */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
      </div>

      {/* Hero Content */}
      <div 
        className={`relative z-20 w-full max-w-5xl text-center transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        {/* Eyebrow */}
        <div className="bg-white/20 text-white text-[11px] sm:text-[12px] font-bold uppercase tracking-widest px-4 py-2 rounded-full inline-block mb-6 border border-white/40 backdrop-blur-sm">
          {eyebrow}
        </div>

        {/* H1 */}
        <h1 className="font-sans font-semibold text-white leading-[1.2] tracking-[-0.02em] mb-4 text-2xl sm:text-4xl md:text-5xl lg:text-6xl">
          {title}
        </h1>

        {/* H2 */}
        <p className="text-white/80 text-sm md:text-lg max-w-3xl mx-auto mb-6 md:mb-10 font-medium leading-relaxed">
          {subtitle}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6 md:mb-10">
          <a href={cta1Link} className="bg-white text-black text-[15px] font-bold rounded-full px-8 py-3.5 hover:scale-[1.03] hover:shadow-[0_0_32px_4px_rgba(255,255,255,0.2)] active:scale-[0.97] transition-all duration-200 flex items-center gap-2 justify-center">
            {cta1Text} <span>→</span>
          </a>
          {cta2Text && (
            <a href={cta2Link} className="bg-transparent border-2 border-white text-white text-[15px] font-bold rounded-full px-8 py-3.5 hover:bg-white/10 transition-all duration-200 inline-flex items-center justify-center">
              {cta2Text}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default WanderfulHero;
